import { toast, type ExternalToast } from 'sonner';
import type { ContactValidationWarning } from '@/types/contact';

interface ContactWarningToastOptions {
  onViewContact?: (contactId: string) => void;
}

interface ContactWarningMatch {
  contactId: string;
  contactName?: string;
}

export interface ContactValidationWarningToast {
  message: string;
  options?: ExternalToast;
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function getLinkedContact(warning: ContactValidationWarning): ContactWarningMatch | null {
  const details = warning.details;
  if (!details) return null;

  const contactId = getString(details.contactId);
  if (contactId) {
    return {
      contactId,
      contactName: getString(details.contactName),
    };
  }

  const contactIds = Array.isArray(details.contactIds) ? details.contactIds : [];
  const firstContactId = getString(contactIds[0]);
  return firstContactId ? { contactId: firstContactId } : null;
}

function viewLabelForMatch(match: ContactWarningMatch): string {
  const name = match.contactName?.trim();
  return name ? `View ${name}` : 'View match';
}

export function buildContactValidationWarningToast(
  warning: ContactValidationWarning,
  { onViewContact }: ContactWarningToastOptions = {},
): ContactValidationWarningToast {
  if (!onViewContact) {
    return {
      message: warning.message,
      options: undefined,
    };
  }

  const match = getLinkedContact(warning);
  if (!match) {
    return {
      message: warning.message,
      options: undefined,
    };
  }

  return {
    message: warning.message,
    options: {
      action: {
        label: viewLabelForMatch(match),
        onClick: () => onViewContact(match.contactId),
      },
    },
  };
}

export function showContactValidationWarningToasts(
  warnings: ContactValidationWarning[] | null | undefined,
  options: ContactWarningToastOptions = {},
) {
  if (!warnings || warnings.length === 0) return;

  for (const warning of warnings) {
    const warningToast = buildContactValidationWarningToast(warning, options);
    toast.warning(warningToast.message, warningToast.options);
  }
}
